/**
 * Notifications Service
 * API service for Daie memo notifications.
 */

import { api } from '@services/api';
import { MEMO_ENDPOINTS, NOTIFICATION_ENDPOINTS } from '@services/api/endpoints';
import type { Notification, NotificationStatus } from '../types';

/**
 * Paginated response type.
 */
interface PaginatedResponse<T> {
  data: T[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

/**
 * Notification API response structure from backend.
 */
interface NotificationApiResponse {
  id: number;
  memoId: number;
  title: string;
  preview: string;
  body: string;
  channel: string;
  sentAt: string;
  status: 'read' | 'unread';
  attachments?: string[];
}

/**
 * Pagination state for notifications list.
 */
export interface NotificationsPagination {
  currentPage: number;
  itemsPerPage: number;
}

function transformNotificationApiResponse(apiNotif: NotificationApiResponse): Notification {
  return {
    id: apiNotif.id,
    memoId: apiNotif.memoId,
    title: apiNotif.title,
    preview: apiNotif.preview,
    body: apiNotif.body,
    channel: apiNotif.channel as Notification['channel'],
    sentAt: apiNotif.sentAt,
    status: apiNotif.status as NotificationStatus,
    attachments: apiNotif.attachments ?? [],
  };
}

/**
 * Notifications API.
 */
export const notificationsApi = {
  /**
   * Fetches notifications for the current user (Daie).
   */
  getNotifications: async (
    pagination: NotificationsPagination = { currentPage: 1, itemsPerPage: 20 }
  ): Promise<PaginatedResponse<Notification>> => {
    const params: Record<string, unknown> = {
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
    };

    const response = await api.get<{
      data: NotificationApiResponse[];
      meta: PaginatedResponse<Notification>['meta'];
    }>(NOTIFICATION_ENDPOINTS.LIST, { params });

    return {
      data: response.data.map(transformNotificationApiResponse),
      meta: response.meta,
    };
  },

  /**
   * Fetches the unread notification count for the current user.
   */
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get<{ count: number }>(NOTIFICATION_ENDPOINTS.UNREAD_COUNT);
    return response.count;
  },

  /**
   * Marks a memo as read for the current user.
   */
  markAsRead: async (memoId: number): Promise<void> => {
    await api.post<void>(MEMO_ENDPOINTS.MARK_READ(memoId));
  },

  /**
   * Marks all notifications as read for the current user.
   */
  markAllAsRead: async (): Promise<void> => {
    await api.post<void>(MEMO_ENDPOINTS.MARK_ALL_READ);
  },
};
